/**
 * Shared error types and utilities for VeilBrowser
 */

import { z } from "zod";
import type { AvailableProfile } from "../types/index.ts";

// ============ API Error Codes ============

/**
 * Top-level API error codes for categorization
 */
export const ApiErrorCode = {
	// Validation errors
	VALIDATION_ERROR: "VALIDATION_ERROR",

	// Resource errors
	RESOURCE_NOT_FOUND: "RESOURCE_NOT_FOUND",
	RESOURCE_CONFLICT: "RESOURCE_CONFLICT",

	// Authentication/Authorization
	AUTHENTICATION_ERROR: "AUTHENTICATION_ERROR",
	AUTHORIZATION_ERROR: "AUTHORIZATION_ERROR",

	/**
	 * The caller is authenticated and authorized but has not enrolled a second
	 * factor, which `/v1/admin/*` requires. Its own code rather than another
	 * `AUTHORIZATION_ERROR` because it is the one refusal on that surface with a
	 * screen to send the operator to, and the console has to tell it apart from
	 * "you are not staff" without reading a message string.
	 */
	TWO_FACTOR_REQUIRED: "TWO_FACTOR_REQUIRED",

	// Server errors
	INTERNAL_ERROR: "INTERNAL_ERROR",
	EXTERNAL_SERVICE_ERROR: "EXTERNAL_SERVICE_ERROR",

	// Rate limiting
	RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
} as const;

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

/**
 * Field-level validation error codes
 */
export const ValidationErrorCode = {
	// Type errors
	INVALID_TYPE: "INVALID_TYPE",
	INVALID_FORMAT: "INVALID_FORMAT",

	// String constraints
	TOO_SHORT: "TOO_SHORT",
	TOO_LONG: "TOO_LONG",
	INVALID_PATTERN: "INVALID_PATTERN",

	// Number constraints
	TOO_SMALL: "TOO_SMALL",
	TOO_BIG: "TOO_BIG",
	NOT_INTEGER: "NOT_INTEGER",

	// Array constraints
	ARRAY_TOO_SHORT: "ARRAY_TOO_SHORT",
	ARRAY_TOO_LONG: "ARRAY_TOO_LONG",

	// Enum/union errors
	INVALID_ENUM_VALUE: "INVALID_ENUM_VALUE",
	INVALID_UNION: "INVALID_UNION",

	// Required/optional
	REQUIRED: "REQUIRED",
	UNRECOGNIZED_KEY: "UNRECOGNIZED_KEY",

	// Custom validation
	CUSTOM: "CUSTOM",
} as const;

export type ValidationErrorCode =
	(typeof ValidationErrorCode)[keyof typeof ValidationErrorCode];

// ============ Zod Schemas for Error Responses ============

/**
 * Schema for a single field validation error
 */
export const ValidationErrorDetailSchema = z.object({
	field: z
		.string()
		.describe("Dot-notation path to the field (e.g., 'proxy.host')"),
	code: z.string().describe("Machine-readable error code"),
	message: z.string().describe("Human-readable error message"),
	received: z.unknown().optional().describe("The value that was received"),
	expected: z.unknown().optional().describe("The expected value or type"),
	constraint: z
		.record(z.string(), z.unknown())
		.optional()
		.describe("Constraint details (min, max, pattern, etc.)"),
});

export type ValidationErrorDetail = z.infer<typeof ValidationErrorDetailSchema>;

/**
 * Schema for the structured error object
 */
export const StructuredErrorSchema = z.object({
	code: z.string().describe("API error code"),
	message: z.string().describe("Human-readable error message"),
	details: z
		.array(ValidationErrorDetailSchema)
		.optional()
		.describe("Field-level error details for validation errors"),
});

export type StructuredError = z.infer<typeof StructuredErrorSchema>;

/**
 * Schema for the full error response
 */
export const ErrorResponseSchema = z.object({
	success: z.literal(false),
	error: StructuredErrorSchema,
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// ============ Error Response Builder ============

/**
 * Build a standardized error response
 */
export function buildErrorResponse(
	code: ApiErrorCode,
	message: string,
	details?: ValidationErrorDetail[],
): ErrorResponse {
	return {
		success: false,
		error: {
			code,
			message,
			...(details && details.length > 0 ? { details } : {}),
		},
	};
}

/**
 * Build a standardized success response
 */
export function buildSuccessResponse<T>(data: T): { success: true; data: T } {
	return {
		success: true,
		data,
	};
}

// ============ Elysia Validation Error Parser ============

/**
 * Map Zod error codes to our validation error codes
 */
function mapZodCodeToValidationCode(
	zodCode: string,
	zodError: unknown,
): ValidationErrorCode {
	const error = zodError as Record<string, unknown>;

	switch (zodCode) {
		case "invalid_type":
			if (error.received === "undefined") {
				return ValidationErrorCode.REQUIRED;
			}
			return ValidationErrorCode.INVALID_TYPE;

		case "too_small":
			if (error.type === "string") {
				return ValidationErrorCode.TOO_SHORT;
			}
			if (error.type === "array") {
				return ValidationErrorCode.ARRAY_TOO_SHORT;
			}
			return ValidationErrorCode.TOO_SMALL;

		case "too_big":
			if (error.type === "string") {
				return ValidationErrorCode.TOO_LONG;
			}
			if (error.type === "array") {
				return ValidationErrorCode.ARRAY_TOO_LONG;
			}
			return ValidationErrorCode.TOO_BIG;

		case "invalid_string":
			return ValidationErrorCode.INVALID_FORMAT;

		case "invalid_enum_value":
			return ValidationErrorCode.INVALID_ENUM_VALUE;

		case "invalid_union":
			return ValidationErrorCode.INVALID_UNION;

		case "unrecognized_keys":
			return ValidationErrorCode.UNRECOGNIZED_KEY;

		case "not_multiple_of":
		case "not_finite":
			return ValidationErrorCode.INVALID_TYPE;

		case "custom":
			return ValidationErrorCode.CUSTOM;

		default:
			return ValidationErrorCode.INVALID_FORMAT;
	}
}

/**
 * Extract constraint information from Zod error
 */
function extractConstraint(
	zodError: Record<string, unknown>,
): Record<string, unknown> | undefined {
	const constraint: Record<string, unknown> = {};

	if ("minimum" in zodError) {
		constraint.min = zodError.minimum;
	}
	if ("maximum" in zodError) {
		constraint.max = zodError.maximum;
	}
	if ("inclusive" in zodError) {
		constraint.inclusive = zodError.inclusive;
	}
	if ("exact" in zodError) {
		constraint.exact = zodError.exact;
	}
	if ("type" in zodError) {
		constraint.type = zodError.type;
	}
	if ("options" in zodError) {
		constraint.allowed = zodError.options;
	}
	if ("validation" in zodError) {
		constraint.validation = zodError.validation;
	}

	return Object.keys(constraint).length > 0 ? constraint : undefined;
}

interface ElysiaValidationErrorItem {
	code: string;
	path: (string | number)[];
	message: string;
	expected?: unknown;
	received?: unknown;
	[key: string]: unknown;
}

interface ElysiaValidationError {
	type: string;
	on: string;
	property?: string;
	message: string;
	found?: unknown;
	errors?: ElysiaValidationErrorItem[];
}

/**
 * Parse Elysia validation error message and transform into structured format
 */
export function parseElysiaValidationError(
	errorMessage: string,
): ValidationErrorDetail[] {
	try {
		const parsed: ElysiaValidationError = JSON.parse(errorMessage);

		if (!parsed.errors || !Array.isArray(parsed.errors)) {
			// Single error without errors array
			return [
				{
					field: parsed.property || "unknown",
					code: ValidationErrorCode.INVALID_FORMAT,
					message: parsed.message,
				},
			];
		}

		return parsed.errors.map((err) => {
			const field = err.path?.join(".") || "unknown";
			const code = mapZodCodeToValidationCode(err.code, err);
			const constraint = extractConstraint(err as Record<string, unknown>);

			const detail: ValidationErrorDetail = {
				field,
				code,
				message: err.message,
			};

			if (err.received !== undefined) {
				detail.received = err.received;
			}

			if (err.expected !== undefined) {
				detail.expected = err.expected;
			}

			if (constraint) {
				detail.constraint = constraint;
			}

			return detail;
		});
	} catch {
		// If parsing fails, return a generic error
		return [
			{
				field: "unknown",
				code: ValidationErrorCode.INVALID_FORMAT,
				message: errorMessage,
			},
		];
	}
}

/**
 * Create a validation error response from Elysia validation error
 */
export function createValidationErrorResponse(
	errorMessage: string,
): ErrorResponse {
	const details = parseElysiaValidationError(errorMessage);

	return buildErrorResponse(
		ApiErrorCode.VALIDATION_ERROR,
		"Request validation failed",
		details,
	);
}

// ============ Base API Error Class ============

/**
 * Base class for API errors with error code support
 */
export abstract class ApiError extends Error {
	public abstract readonly code: ApiErrorCode;
	public abstract readonly statusCode: number;

	/**
	 * Convert error to structured API response format
	 */
	abstract toResponse(): ErrorResponse;
}

// ============ Specific Error Classes ============

export class UnauthorizedError extends ApiError {
	public readonly code = ApiErrorCode.AUTHORIZATION_ERROR;
	public readonly statusCode = 401;

	constructor(message: string = "Unauthorized") {
		super(message);
		this.name = "UnauthorizedError";
	}

	toResponse(): ErrorResponse {
		return buildErrorResponse(this.code, this.message);
	}
}

/**
 * Error thrown when a requested profile is not found
 */
export class ProfileNotFoundError extends ApiError {
	public readonly code = ApiErrorCode.RESOURCE_NOT_FOUND;
	public readonly statusCode = 400;
	public readonly os: string;
	public readonly browserVersion: number;
	public readonly availableProfiles: AvailableProfile[];

	constructor(
		os: string,
		browserVersion: number,
		availableProfiles: AvailableProfile[],
	) {
		super(`Profile not found for os=${os}, browserVersion=${browserVersion}`);
		this.name = "ProfileNotFoundError";
		this.os = os;
		this.browserVersion = browserVersion;
		this.availableProfiles = availableProfiles;
	}

	toResponse(): ErrorResponse {
		const details: ValidationErrorDetail[] = [
			{
				field: "fingerprint",
				code: "RESOURCE_NOT_FOUND",
				message: `No profile found for OS '${this.os}' with browser version ${this.browserVersion}`,
				received: { os: this.os, browserVersion: this.browserVersion },
				constraint: {
					availableVersions: this.availableProfiles.map((p) => p.version),
				},
			},
		];

		return buildErrorResponse(this.code, this.message, details);
	}
}

/**
 * Error thrown when a browser version folder is not found
 */
export class VersionNotFoundError extends ApiError {
	public readonly code = ApiErrorCode.RESOURCE_NOT_FOUND;
	public readonly statusCode = 400;
	public readonly browserVersion: number;
	public readonly availableVersions: number[];

	constructor(browserVersion: number, availableVersions: number[]) {
		super(`Browser version ${browserVersion} not found`);
		this.name = "VersionNotFoundError";
		this.browserVersion = browserVersion;
		this.availableVersions = availableVersions;
	}

	toResponse(): ErrorResponse {
		const details: ValidationErrorDetail[] = [
			{
				field: "fingerprint.browserVersion",
				code: "RESOURCE_NOT_FOUND",
				message: `Browser version ${this.browserVersion} is not available`,
				received: this.browserVersion,
				constraint: {
					availableVersions: this.availableVersions,
				},
			},
		];

		return buildErrorResponse(this.code, this.message, details);
	}
}

/**
 * Error thrown when the VeilBrowser executable is not found
 */
export class VeilBrowserNotFoundError extends ApiError {
	public readonly code = ApiErrorCode.EXTERNAL_SERVICE_ERROR;
	public readonly statusCode = 500;

	constructor() {
		super("VeilBrowser executable not found");
		this.name = "VeilBrowserNotFoundError";
	}

	toResponse(): ErrorResponse {
		return buildErrorResponse(
			this.code,
			"Browser service is unavailable. Please contact support.",
		);
	}
}

/**
 * Error thrown when authentication fails
 */
export class AuthenticationError extends ApiError {
	public readonly code = ApiErrorCode.AUTHENTICATION_ERROR;
	public readonly statusCode = 401;

	constructor(message: string = "Invalid or missing API key") {
		super(message);
		this.name = "AuthenticationError";
	}

	toResponse(): ErrorResponse {
		return buildErrorResponse(this.code, this.message);
	}
}

/**
 * Error thrown when a session is not found
 */
export class SessionNotFoundError extends ApiError {
	public readonly code = ApiErrorCode.RESOURCE_NOT_FOUND;
	public readonly statusCode = 404;
	public readonly sessionId: string;

	constructor(sessionId: string) {
		super(`Session not found: ${sessionId}`);
		this.name = "SessionNotFoundError";
		this.sessionId = sessionId;
	}

	toResponse(): ErrorResponse {
		const details: ValidationErrorDetail[] = [
			{
				field: "id",
				code: "RESOURCE_NOT_FOUND",
				message: `Session '${this.sessionId}' does not exist or has been terminated`,
				received: this.sessionId,
			},
		];

		return buildErrorResponse(this.code, this.message, details);
	}
}

// ============ SaaS API Error Classes ============

/**
 * Error thrown when no active organization is set for the user
 */
export class NoActiveOrganizationError extends ApiError {
	public readonly code = ApiErrorCode.AUTHORIZATION_ERROR;
	public readonly statusCode = 400;

	constructor() {
		super("No active organization. Please select an organization.");
		this.name = "NoActiveOrganizationError";
	}

	toResponse(): ErrorResponse {
		return buildErrorResponse(this.code, this.message);
	}
}

/**
 * Error thrown when a SaaS profile is not found
 */
export class SaasProfileNotFoundError extends ApiError {
	public readonly code = ApiErrorCode.RESOURCE_NOT_FOUND;
	public readonly statusCode = 404;
	public readonly profileId: string;

	constructor(profileId: string) {
		super(`Profile not found: ${profileId}`);
		this.name = "SaasProfileNotFoundError";
		this.profileId = profileId;
	}

	toResponse(): ErrorResponse {
		const details: ValidationErrorDetail[] = [
			{
				field: "id",
				code: "RESOURCE_NOT_FOUND",
				message: `Profile '${this.profileId}' does not exist`,
				received: this.profileId,
			},
		];

		return buildErrorResponse(this.code, this.message, details);
	}
}

/**
 * Error thrown when a resource with the same name already exists
 */
export class ResourceConflictError extends ApiError {
	public readonly code = ApiErrorCode.RESOURCE_CONFLICT;
	public readonly statusCode = 409;
	public readonly resourceType: string;
	public readonly field: string;
	public readonly value: string;

	constructor(resourceType: string, field: string, value: string) {
		super(`${resourceType} with this ${field} already exists`);
		this.name = "ResourceConflictError";
		this.resourceType = resourceType;
		this.field = field;
		this.value = value;
	}

	toResponse(): ErrorResponse {
		const details: ValidationErrorDetail[] = [
			{
				field: this.field,
				code: "RESOURCE_CONFLICT",
				message: `${this.resourceType} with ${this.field} '${this.value}' already exists`,
				received: this.value,
			},
		];

		return buildErrorResponse(this.code, this.message, details);
	}
}

/**
 * Error thrown when profile limit is reached for an organization
 */
export class ProfileLimitExceededError extends ApiError {
	public readonly code = ApiErrorCode.AUTHORIZATION_ERROR;
	public readonly statusCode = 403;
	public readonly currentCount: number;
	public readonly limit: number;

	constructor(currentCount: number, limit: number) {
		super(
			`Profile limit reached. Current: ${currentCount}, Limit: ${limit}. Please upgrade your plan.`,
		);
		this.name = "ProfileLimitExceededError";
		this.currentCount = currentCount;
		this.limit = limit;
	}

	toResponse(): ErrorResponse {
		const details: ValidationErrorDetail[] = [
			{
				field: "profiles",
				code: "LIMIT_EXCEEDED",
				message: this.message,
				constraint: {
					current: this.currentCount,
					limit: this.limit,
				},
			},
		];

		return buildErrorResponse(this.code, this.message, details);
	}
}

/**
 * Error thrown when an organization is not found
 */
export class OrganizationNotFoundError extends ApiError {
	public readonly code = ApiErrorCode.RESOURCE_NOT_FOUND;
	public readonly statusCode = 404;
	public readonly organizationId: string;

	constructor(organizationId: string) {
		super(`Organization not found: ${organizationId}`);
		this.name = "OrganizationNotFoundError";
		this.organizationId = organizationId;
	}

	toResponse(): ErrorResponse {
		const details: ValidationErrorDetail[] = [
			{
				field: "id",
				code: "RESOURCE_NOT_FOUND",
				message: `Organization '${this.organizationId}' does not exist`,
				received: this.organizationId,
			},
		];

		return buildErrorResponse(this.code, this.message, details);
	}
}

/**
 * Error thrown when user doesn't have permission for an action
 */
export class InsufficientPermissionsError extends ApiError {
	public readonly code = ApiErrorCode.AUTHORIZATION_ERROR;
	public readonly statusCode = 403;
	public readonly requiredRole: string;

	constructor(requiredRole: string = "admin") {
		super(`Insufficient permissions. Required role: ${requiredRole}`);
		this.name = "InsufficientPermissionsError";
		this.requiredRole = requiredRole;
	}

	toResponse(): ErrorResponse {
		return buildErrorResponse(this.code, this.message);
	}
}
